import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaArrowLeft,
  FaBan,
  FaBell,
  FaCheck,
  FaCheckCircle,
  FaClipboardCheck,
  FaClock,
  FaExclamationTriangle,
  FaMapMarkerAlt,
  FaMoon,
  FaShieldAlt,
  FaSun,
  FaTint,
} from 'react-icons/fa';
import {
  dismissLocalOperationalNotification,
  listLocalOperationalNotifications,
  markAllLocalOperationalNotificationsRead,
  markLocalOperationalNotificationRead,
  seedLocalOperationalNotifications,
  subscribeLocalOperationalNotifications,
  deduplicateOperationalNotifications,
} from '../services/operationalNotificationService';
import {
  cadernoRuntime,
  dismissCadernoNotification,
  listIrrigationNotifications,
  listCadernoNotifications,
  markIrrigationNotificationRead,
  markCadernoNotificationRead,
} from '../services/cadernoService';
import { plotMatchesLegacyId } from '../services/operationalPropertiesService';
import './OperationalNotificationsCenter.css';

const FILTERS = [
  { key: 'all', label: 'Todas' },
  { key: 'unread', label: 'Não lidas' },
  { key: 'tasks', label: 'Minhas tarefas' },
  { key: 'approval', label: 'Aprovações' },
  { key: 'irrigation', label: 'Irrigação' },
];

const TYPE_META = {
  task_assigned: { label: 'Tarefa atribuída', icon: FaClipboardCheck, tone: 'task' },
  deadline: { label: 'Prazo em atenção', icon: FaClock, tone: 'deadline' },
  approval: { label: 'Aprovação solicitada', icon: FaCheckCircle, tone: 'approval' },
  correction: { label: 'Correção solicitada', icon: FaExclamationTriangle, tone: 'correction' },
  completed: { label: 'Atividade concluída', icon: FaCheckCircle, tone: 'completed' },
  information: { label: 'Registro operacional', icon: FaShieldAlt, tone: 'information' },
  irrigation: { label: 'Manejo de irrigação', icon: FaTint, tone: 'irrigation' },
};

const DB_TYPE_TO_UI = {
  tarefa_atribuida: 'task_assigned',
  prazo: 'deadline',
  aprovacao: 'approval',
  correcao: 'correction',
  conclusao: 'completed',
  informacao: 'information',
};

const DB_PRIORITY_TO_UI = {
  urgente: 'urgent',
  alta: 'high',
  normal: 'normal',
  baixa: 'low',
};

function mapDatabaseNotification(row, propertiesById) {
  const property = propertiesById.get(String(row.propriedade_id));
  const plot = property?.plots?.find(item => plotMatchesLegacyId(item, row.talhao_id));
  return {
    id: row.id,
    type: DB_TYPE_TO_UI[row.tipo] || 'information',
    title: row.titulo,
    message: row.mensagem,
    priority: DB_PRIORITY_TO_UI[row.prioridade] || 'normal',
    propertyId: row.propriedade_id,
    propertyName: property?.name || 'Propriedade',
    plotId: row.talhao_id,
    plotName: row.talhao_id == null ? 'Toda a propriedade' : (plot?.name || 'Talhão'),
    orderId: row.ordem_id,
    orderCode: row.dados?.codigo || null,
    action: 'caderno',
    createdAt: row.criada_em,
    readAt: row.lida_em,
    isPersisted: true,
    persistenceSource: 'caderno',
  };
}

function mapIrrigationNotification(row, propertiesById) {
  const property = propertiesById.get(String(row.dados?.propriedade_id));
  const plot = property?.plots?.find(item => plotMatchesLegacyId(item, row.dados?.talhao_id));
  return {
    id: row.id,
    type: 'irrigation',
    title: row.titulo,
    message: row.mensagem,
    priority: row.dados?.prioridade || 'normal',
    propertyId: row.dados?.propriedade_id,
    propertyName: row.dados?.propriedade_nome || property?.name || 'Propriedade',
    plotId: row.dados?.talhao_id,
    plotName: row.dados?.talhao_nome || plot?.name || 'Talhão',
    action: 'irrigacao',
    createdAt: row.criado_em,
    readAt: row.lida ? row.criado_em : null,
    isPersisted: true,
    persistenceSource: 'irrigacao',
  };
}

function formatRelativeDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Data não informada';
  const minutes = Math.max(0, Math.round((Date.now() - date.getTime()) / 60000));
  if (minutes < 1) return 'Agora';
  if (minutes < 60) return `Há ${minutes} min`;
  if (minutes < 24 * 60) return `Há ${Math.floor(minutes / 60)} h`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export default function OperationalNotificationsCenter({
  darkMode,
  setDarkMode,
  currentUserId,
  currentUserName,
  realProperties = [],
  realContextLoading = false,
  realContextError = '',
  onNavigate,
}) {
  const [notifications, setNotifications] = useState([]);
  const [filter, setFilter] = useState('all');
  const [propertyFilter, setPropertyFilter] = useState('all');
  const [feedback, setFeedback] = useState('');
  const [realNotificationsLoading, setRealNotificationsLoading] = useState(false);
  const [realNotificationsError, setRealNotificationsError] = useState('');

  const propertiesById = useMemo(
    () => new Map(realProperties.map(property => [String(property.id), property])),
    [realProperties]
  );

  const loadRealNotifications = useCallback(async () => {
    setRealNotificationsLoading(true);
    setRealNotificationsError('');
    try {
      const [cadernoResult, irrigationResult] = await Promise.allSettled([
        listCadernoNotifications(),
        listIrrigationNotifications(),
      ]);
      const cadernoRows = cadernoResult.status === 'fulfilled' ? cadernoResult.value : [];
      const irrigationRows = irrigationResult.status === 'fulfilled' ? irrigationResult.value : [];
      if (cadernoResult.status === 'rejected' && irrigationResult.status === 'rejected') {
        throw cadernoResult.reason;
      }
      // Propriedades excluídas (arquivadas) não devem gerar pendências pendentes.
      const visibleCadernoRows = cadernoRows.filter(row => !row.propriedade_id || propertiesById.has(String(row.propriedade_id)));
      const visibleIrrigationRows = irrigationRows.filter(row => !row.dados?.propriedade_id || propertiesById.has(String(row.dados.propriedade_id)));
      setNotifications(deduplicateOperationalNotifications([
        ...visibleCadernoRows.map(row => mapDatabaseNotification(row, propertiesById)),
        ...visibleIrrigationRows.map(row => mapIrrigationNotification(row, propertiesById)),
      ]).sort((first, second) => new Date(second.createdAt) - new Date(first.createdAt)));
    } catch (error) {
      setNotifications([]);
      setRealNotificationsError(error?.message || 'Não foi possível carregar as notificações reais.');
    } finally {
      setRealNotificationsLoading(false);
    }
  }, [propertiesById]);

  const reload = useCallback(() => {
    if (cadernoRuntime.readsEnabled) {
      loadRealNotifications();
      return;
    }
    setNotifications(listLocalOperationalNotifications(currentUserId));
  }, [currentUserId, loadRealNotifications]);

  useEffect(() => {
    if (!currentUserId || realContextLoading || realContextError) return undefined;

    if (cadernoRuntime.readsEnabled) {
      loadRealNotifications();
      return undefined;
    }

    seedLocalOperationalNotifications({
      userId: currentUserId,
      userName: currentUserName,
      properties: realProperties,
    });
    reload();
    return subscribeLocalOperationalNotifications(currentUserId, reload);
  }, [currentUserId, currentUserName, realProperties, realContextLoading, realContextError, reload, loadRealNotifications]);

  const counts = useMemo(() => ({
    unread: notifications.filter(item => !item.readAt).length,
    urgent: notifications.filter(item => !item.readAt && ['urgent', 'high'].includes(item.priority)).length,
    approval: notifications.filter(item => !item.readAt && item.type === 'approval').length,
    history: notifications.filter(item => Boolean(item.readAt)).length,
  }), [notifications]);

  const visibleNotifications = useMemo(() => notifications.filter(notification => {
    if (propertyFilter !== 'all' && String(notification.propertyId) !== propertyFilter) return false;
    if (filter === 'unread') return !notification.readAt;
    if (filter === 'tasks') return ['task_assigned', 'deadline', 'correction'].includes(notification.type);
    if (filter === 'approval') return notification.type === 'approval';
    if (filter === 'irrigation') return notification.type === 'irrigation';
    return true;
  }), [notifications, filter, propertyFilter]);

  const toggleRead = async notification => {
    const nextRead = !notification.readAt;
    if (notification.isPersisted) {
      try {
        if (notification.persistenceSource === 'irrigacao') {
          await markIrrigationNotificationRead(notification.id, nextRead);
        } else {
          await markCadernoNotificationRead(notification.id, nextRead);
        }
        await loadRealNotifications();
      } catch (error) {
        setFeedback(error?.message || 'Não foi possível atualizar a notificação.');
        return;
      }
    } else {
      markLocalOperationalNotificationRead(currentUserId, notification.id, nextRead);
    }
    setFeedback(notification.readAt ? 'Notificação marcada como não lida.' : 'Notificação marcada como lida.');
  };

  const openNotification = async notification => {
    if (!notification.readAt) {
      if (notification.isPersisted) {
        try {
          if (notification.persistenceSource === 'irrigacao') {
            await markIrrigationNotificationRead(notification.id, true);
          } else {
            await markCadernoNotificationRead(notification.id, true);
          }
          await loadRealNotifications();
        } catch (error) {
          setFeedback(error?.message || 'Não foi possível marcar a notificação como lida.');
        }
      } else {
        markLocalOperationalNotificationRead(currentUserId, notification.id, true);
      }
    }
    onNavigate(notification.action || 'caderno', {
      orderId: notification.orderId,
      propertyId: notification.propertyId,
      plotId: notification.plotId,
    });
  };

  const dismissNotification = async notification => {
    const confirmed = window.confirm(
      'Dispensar este aviso? Ele deixará de aparecer no Dashboard e nas Notificações, mas a ordem e o histórico do Caderno de Campo não serão alterados.'
    );
    if (!confirmed) return;

    try {
      if (notification.isPersisted) {
        await dismissCadernoNotification(notification.id);
        await loadRealNotifications();
      } else {
        dismissLocalOperationalNotification(currentUserId, notification.id);
        reload();
      }
      setFeedback('Aviso dispensado. A ordem e o histórico do Caderno de Campo foram preservados.');
    } catch (error) {
      setFeedback(error?.message || 'Não foi possível dispensar este aviso.');
    }
  };

  const markAllRead = async () => {
    if (cadernoRuntime.readsEnabled) {
      const unread = notifications.filter(item => !item.readAt);
      try {
        await Promise.all(unread.map(item => item.persistenceSource === 'irrigacao'
          ? markIrrigationNotificationRead(item.id, true)
          : markCadernoNotificationRead(item.id, true)));
        await loadRealNotifications();
      } catch (error) {
        setFeedback(error?.message || 'Não foi possível marcar todas como lidas.');
        return;
      }
    } else {
      markAllLocalOperationalNotificationsRead(currentUserId);
    }
    setFeedback('Todas as notificações operacionais foram marcadas como lidas.');
  };

  return (
    <div className={`operational-notifications-shell ${darkMode ? 'is-dark' : ''}`}>
      <header className="operational-notifications-topbar">
        <button type="button" onClick={() => onNavigate('dashboard')} aria-label="Voltar ao painel"><FaArrowLeft /></button>
        <div className="operational-notifications-brand">
          <span><FaBell /></span>
          <div><strong>Notificações operacionais</strong><small>Tarefas, prazos, aprovações e manejo</small></div>
        </div>
        <div className="operational-notifications-account"><FaShieldAlt /><span><strong>{currentUserName}</strong><small>Caixa individual da conta</small></span></div>
        <button type="button" onClick={() => setDarkMode(value => !value)} aria-label={darkMode ? 'Ativar tema claro' : 'Ativar tema escuro'}>{darkMode ? <FaSun /> : <FaMoon />}</button>
      </header>

      <main className="operational-notifications-main">
        {(realNotificationsLoading || realNotificationsError) && (
          <section className="operational-notifications-safety" role="status">
            <FaShieldAlt />
            <div>
              <span>{realNotificationsLoading ? 'Carregando notificações do banco de produção…' : realNotificationsError}</span>
            </div>
          </section>
        )}

        <section className="operational-notifications-hero">
          <div>
            <span>Rotina organizada</span>
            <h1>O que precisa da sua atenção</h1>
            <p>Consulte cada pendência com o contexto exato de propriedade, talhão e ordem de serviço.</p>
          </div>
          <div className="operational-notifications-metrics" aria-label="Resumo das notificações">
            <div><small>Não lidas</small><strong>{counts.unread}</strong><span>pendências</span></div>
            <div className="is-urgent"><small>Prioritárias</small><strong>{counts.urgent}</strong><span>exigem atenção</span></div>
            <div className="is-approval"><small>Aprovações</small><strong>{counts.approval}</strong><span>para revisar</span></div>
            <div className="is-history"><small>Lidas</small><strong>{counts.history}</strong><span>no histórico</span></div>
          </div>
        </section>

        {feedback && <div className="operational-notifications-feedback"><FaCheck /> <span>{feedback}</span><button type="button" onClick={() => setFeedback('')}>Fechar</button></div>}

        <section className="operational-notifications-board">
          <div className="operational-notifications-board-heading">
            <div><span>Caixa de entrada</span><h2>Atividades da sua conta</h2></div>
            {counts.unread > 0 && <button type="button" onClick={markAllRead}><FaCheck /> Marcar todas como lidas</button>}
          </div>

          <div className="operational-notifications-toolbar">
            <div className="operational-notifications-filters" aria-label="Filtrar notificações">
              {FILTERS.map(item => (
                <button type="button" key={item.key} className={filter === item.key ? 'is-active' : ''} aria-pressed={filter === item.key} onClick={() => setFilter(item.key)}>{item.label}</button>
              ))}
            </div>
            <label>
              <FaMapMarkerAlt />
              <select value={propertyFilter} onChange={event => setPropertyFilter(event.target.value)} aria-label="Filtrar notificações por propriedade">
                <option value="all">Todas as propriedades</option>
                {realProperties.map(property => <option value={String(property.id)} key={property.id}>{property.name}</option>)}
              </select>
            </label>
          </div>

          {realContextLoading ? (
            <div className="operational-notifications-empty"><FaClock /><strong>Carregando o contexto da conta</strong><span>Aguarde enquanto confirmamos as propriedades autorizadas.</span></div>
          ) : realContextError ? (
            <div className="operational-notifications-empty is-error"><FaExclamationTriangle /><strong>Contexto indisponível</strong><span>{realContextError}</span></div>
          ) : visibleNotifications.length === 0 ? (
            <div className="operational-notifications-empty"><FaCheckCircle /><strong>Nenhuma pendência neste filtro</strong><span>Sua rotina está em dia nesta categoria.</span></div>
          ) : (
            <div className="operational-notifications-list">
              {visibleNotifications.map(notification => {
                const meta = TYPE_META[notification.type] || TYPE_META.information;
                const Icon = meta.icon;
                return (
                  <article className={`${notification.readAt ? 'is-read' : 'is-unread'} is-${meta.tone}`} key={notification.id}>
                    <button type="button" className="operational-notification-open" onClick={() => openNotification(notification)}>
                      <span className="operational-notification-icon"><Icon /></span>
                      <div className="operational-notification-content">
                        <div className="operational-notification-meta"><span>{meta.label}</span><time>{formatRelativeDate(notification.createdAt)}</time></div>
                        <h3>{notification.title}</h3>
                        <p>{notification.message}</p>
                        <div className="operational-notification-context"><strong>{notification.propertyName}</strong><span>{notification.plotName}</span>{(notification.orderCode || notification.orderId) && <em>{notification.orderCode || `Ordem ${String(notification.orderId).slice(0, 8).toUpperCase()}`}</em>}</div>
                      </div>
                      {!notification.readAt && <span className="operational-notification-dot" aria-label="Não lida" />}
                    </button>
                    <div className="operational-notification-actions">
                      {notification.persistenceSource !== 'irrigacao' && (
                        <button type="button" className="is-dismiss" onClick={() => dismissNotification(notification)}><FaBan /> Dispensar aviso</button>
                      )}
                      <button type="button" onClick={() => toggleRead(notification)}>{notification.readAt ? 'Marcar como não lida' : 'Marcar como lida'}</button>
                      <button type="button" onClick={() => openNotification(notification)}>{notification.type === 'irrigation' ? 'Abrir Irrigação' : 'Abrir no Caderno'}</button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
